export interface SelectionGroupItem {
    item_id: number;
    item_name: string;
    price_modifier: number;
    delivery_price_modifier?: number;
    online_price_modifier?: number;
    desk_price_modifier?: number;
    min_selections: number;
    max_selections: number;
  }
  
  export interface SelectionGroup {
    selection_group_id: number;
    selection_group_name: string;
    selection_group_description: string;
    is_price_modifier: boolean;
    min_variance: number;
    max_variance: number;
    min_selections: number;
    max_selections: number;
    items: SelectionGroupItem[];
  }
  
  export interface ModifierOption {
    option_id: number;
    option_name: string;
    option_description: string;
    price_modifier: number;
    delivery_price_modifier?: number;
    online_price_modifier?: number;
    desk_price_modifier?: number;
  }
  
  export interface Modifier {
    modifier_id: number;
    modifier_name: string;
    modifier_description: string;
    is_optional: boolean;
    is_price_setter: boolean;
    options: ModifierOption[];
  }
  
  export interface Item {
    item_id: number;
    parent_item_id?: number;
    item_name: string;
    label: string;
    price: number;
    is_delivery?: boolean;
    price_delivery?: number;
    description?: string;
    main_image?: string;
    isCombo?: boolean;
    image_url?: string;
    image_list?: string[];
    state?: boolean;
    gluten?: boolean;
    vegano?: boolean;
    sodio?: boolean;
    lactosa?: boolean;
    azucar?: boolean;
    vegetariano?: boolean;
    alcohol?: boolean;
    picante?: boolean;
    units?: number;
    is_online?: boolean;
    price_online?: number;
    is_desk?: boolean;
    price_desk?: number;
    category_id?: number;
    workstation_id?: number;
    local_id?: number;
    unidad_medida_id?: number;
    provider_id?: number;
    stock_control?: boolean;
    selection_groups: SelectionGroup[];
    modifiers: Modifier[];
  }

  export const pedidoTypeMap: Record<string, number> = {
    Delivery: 3,
    Caja: 3, // Both Delivery and Pickup use pedido_type_id = 3
    Restaurant: 1,
    Autoservicio: 2,
  };

  /**
   * Get the correct price for an item based on the pedido type.
   * Falls back to the base price if the specific price field is not available.
   * 
   * @param item - The menu item
   * @param pedidoType - The type of order (Restaurant, Autoservicio, Delivery, Caja)
   * @returns The appropriate price for the given pedido type
   */
  export const getItemPrice = (item: Item, pedidoType: string): number => {
    const pedidoTypeId = pedidoTypeMap[pedidoType];
    
    switch (pedidoTypeId) {
      case 1: // Restaurant - uses is_desk/price_desk
        return item.price_desk ?? item.price;
      case 2: // Autoservicio - uses is_online/price_online
        return item.price_online ?? item.price;
      case 3: // Delivery/Caja - uses is_delivery/price_delivery
        return item.price_delivery ?? item.price;
      default:
        return item.price;
    }
  };

  /**
   * Check if an item is available for a specific pedido type.
   * 
   * @param item - The menu item
   * @param pedidoType - The type of order (Restaurant, Autoservicio, Delivery, Caja)
   * @returns true if the item is available for the given pedido type
   */
  export const isItemAvailable = (item: Item, pedidoType: string): boolean => {
    const pedidoTypeId = pedidoTypeMap[pedidoType];
    
    switch (pedidoTypeId) {
      case 1: // Restaurant - checks is_desk
        return item.state ?? true; // Default to true if not specified
      case 2: // Autoservicio - checks is_online
        return item.is_online ?? true;
      case 3: // Delivery/Caja - checks is_delivery
        return item.is_delivery ?? true;
      case 4:
        return item.is_desk ?? true;
      case 5:
        return item.is_desk ?? true;
      default:
        return true;
    }
  };

  /**
   * Get the correct price modifier for a selection group item based on the pedido type.
   * Falls back to the base price_modifier if the specific modifier field is not available.
   * 
   * @param selectionItem - The selection group item
   * @param pedidoType - The type of order (Restaurant, Autoservicio, Delivery, Caja)
   * @returns The appropriate price modifier for the given pedido type
   */
  export const getSelectionItemPriceModifier = (
    selectionItem: SelectionGroupItem, 
    pedidoType: string
  ): number => {
    const pedidoTypeId = pedidoTypeMap[pedidoType];
    
    switch (pedidoTypeId) {
      case 1: // Restaurant - uses desk_price_modifier
        return selectionItem.desk_price_modifier ?? selectionItem.price_modifier;
      case 2: // Autoservicio - uses online_price_modifier
        return selectionItem.online_price_modifier ?? selectionItem.price_modifier;
      case 3: // Delivery/Caja - uses delivery_price_modifier
        return selectionItem.delivery_price_modifier ?? selectionItem.price_modifier;
      default:
        return selectionItem.price_modifier;
    }
  };

  /**
   * Get the correct price modifier for a modifier option based on the pedido type.
   * Falls back to the base price_modifier if the specific modifier field is not available.
   * 
   * @param modifierOption - The modifier option
   * @param pedidoType - The type of order (Restaurant, Autoservicio, Delivery, Caja)
   * @returns The appropriate price modifier for the given pedido type
   */
  export const getModifierOptionPriceModifier = (
    modifierOption: ModifierOption, 
    pedidoType: string
  ): number => {
    const pedidoTypeId = pedidoTypeMap[pedidoType];
    
    switch (pedidoTypeId) {
      case 1: // Restaurant - uses desk_price_modifier
        return modifierOption.desk_price_modifier ?? modifierOption.price_modifier;
      case 2: // Autoservicio - uses online_price_modifier
        return modifierOption.online_price_modifier ?? modifierOption.price_modifier;
      case 3: // Delivery/Caja - uses delivery_price_modifier
        return modifierOption.delivery_price_modifier ?? modifierOption.price_modifier;
      default:
        return modifierOption.price_modifier;
    }
  };
  
  // Fake data simulating what we would receive from the server
  export const fakeCategoryItems: Item[] = [
    {
      item_id: 101,
      item_name: "Hamburguesa Especial",
      label: "Hamburguesa Especial",
      price: 10.00,
      price_delivery: 11.00,
      price_online: 10.50,
      price_desk: 9.50,
      is_delivery: true,
      is_online: true,
      is_desk: true,
      description: "Deliciosa hamburguesa con ingredientes frescos",
      selection_groups: [
        {
          selection_group_id: 1,
          selection_group_name: "Tamaños Hamburguesa 1",
          selection_group_description: "Elige el tamaño de tu hamburguesa",
          is_price_modifier: false,
          min_variance: 1,
          max_variance: 1,
          min_selections: 1,
          max_selections: 1,
          items: [
            {
              item_id: 201,
              item_name: "Chico",
              price_modifier: 0.00,
              min_selections: 1,
              max_selections: 1
            },
            {
              item_id: 202,
              item_name: "Mediano",
              price_modifier: 2.00,
              min_selections: 1,
              max_selections: 1
            },
            {
              item_id: 203,
              item_name: "Grande",
              price_modifier: 4.00,
              min_selections: 1,
              max_selections: 1
            }
          ]
        },
        {
          selection_group_id: 2,
          selection_group_name: "Variedad de Wings",
          selection_group_description: "Elije 10 wings, puedes elegir más de uno, maximo 3 diferentes",
          is_price_modifier: false,
          min_variance: 1,
          max_variance: 3,
          min_selections: 10,
          max_selections: 10,
          items: [
            { item_id: 204, item_name: "Buffalo", price_modifier: 0.00, min_selections: 0, max_selections: 10 },
            { item_id: 205, item_name: "BBQ", price_modifier: 0.00, min_selections: 0, max_selections: 10 },
            { item_id: 206, item_name: "Teriyaki", price_modifier: 0.00, min_selections: 0, max_selections: 10 },
            { item_id: 207, item_name: "Limon", price_modifier: 0.00, min_selections: 0, max_selections: 10 },
            { item_id: 208, item_name: "Ajo", price_modifier: 0.00, min_selections: 0, max_selections: 10 },
            { item_id: 209, item_name: "Miel", price_modifier: 0.00, min_selections: 0, max_selections: 10 },
            { item_id: 210, item_name: "Cajun", price_modifier: 0.00, min_selections: 0, max_selections: 10 },
            { item_id: 211, item_name: "Chipotle", price_modifier: 0.00, min_selections: 0, max_selections: 10 },
            { item_id: 212, item_name: "Habanero", price_modifier: 0.00, min_selections: 0, max_selections: 10 },
            { item_id: 213, item_name: "Mango", price_modifier: 0.00, min_selections: 0, max_selections: 10 },
            { item_id: 214, item_name: "Pina", price_modifier: 0.00, min_selections: 0, max_selections: 10 },
            { item_id: 215, item_name: "Ranch", price_modifier: 0.00, min_selections: 0, max_selections: 10 },
            { item_id: 216, item_name: "Blue Cheese", price_modifier: 0.00, min_selections: 0, max_selections: 10 },
            { item_id: 217, item_name: "Salsa de Tomate", price_modifier: 0.00, min_selections: 0, max_selections: 10 },
            { item_id: 218, item_name: "Salsa de Mostaza", price_modifier: 0.00, min_selections: 0, max_selections: 10 }
          ]
        }
      ],
      modifiers: [
        {
          modifier_id: 1,
          modifier_name: "Extra Queso",
          modifier_description: "Agrega extra queso a tu hamburguesa",
          is_optional: true,
          is_price_setter: false,
          options: [
            {
              option_id: 1,
              option_name: "Cheddar",
              option_description: "Queso cheddar extra",
              price_modifier: 1.00
            },
            {
              option_id: 2,
              option_name: "Mozzarella",
              option_description: "Mozzarella extra",
              price_modifier: 1.50
            }
          ]
        },
        {
          modifier_id: 2,
          modifier_name: "Tipo de Pan",
          modifier_description: "Selecciona el tipo de pan para tu hamburguesa",
          is_optional: false,
          is_price_setter: false,
          options: [
            {
              option_id: 3,
              option_name: "Pan Blanco",
              option_description: "Clásico pan blanco",
              price_modifier: 0.00
            },
            {
              option_id: 4,
              option_name: "Pan Integral",
              option_description: "Pan integral",
              price_modifier: 0.50
            },
            {
              option_id: 5,
              option_name: "Pan Sin Gluten",
              option_description: "Pan apto para celíacos",
              price_modifier: 1.00
            }
          ]
        }
      ]
    },
    {
      item_id: 102,
      item_name: "Combo de Nuggets",
      label: "Combo de Nuggets",
      price: 8.00,
      price_delivery: 8.50,
      price_online: 8.20,
      price_desk: 7.50,
      is_delivery: true,
      is_online: true,
      is_desk: true,
      description: "Combo de 10 nuggets con salsa a elección",
      selection_groups: [
        {
          selection_group_id: 3,
          selection_group_name: "Salsas para Nuggets",
          selection_group_description: "Elige hasta 2 salsas",
          is_price_modifier: false,
          min_variance: 1,
          max_variance: 2,
          min_selections: 1,
          max_selections: 2,
          items: [
            { item_id: 301, item_name: "BBQ", price_modifier: 0.00, min_selections: 0, max_selections: 2 },
            { item_id: 302, item_name: "Mostaza", price_modifier: 0.00, min_selections: 0, max_selections: 2 },
            { item_id: 303, item_name: "Ketchup", price_modifier: 0.00, min_selections: 0, max_selections: 2 },
            { item_id: 304, item_name: "Agridulce", price_modifier: 0.00, min_selections: 0, max_selections: 2 }
          ]
        }
      ],
      modifiers: [
        {
          modifier_id: 3,
          modifier_name: "Extra Dips",
          modifier_description: "Agrega dips extra a tu combo",
          is_optional: true,
          is_price_setter: false,
          options: [
            {
              option_id: 6,
              option_name: "Guacamole",
              option_description: "Guacamole fresca",
              price_modifier: 1.00
            },
            {
              option_id: 7,
              option_name: "Sriracha",
              option_description: "Salsa picante Sriracha",
              price_modifier: 0.50
            }
          ]
        }
      ]
    }
  ];

    // completely different from the other snippets
  export const fakeCategoryItems2: Item[] = [
    {
      item_id: 200,
      item_name: "Cocacola Normal",
      label: "Cocacola Normal",
      price: 2.00,
      price_delivery: 2.50,
      price_online: 2.20,
      price_desk: 1.50,
      is_delivery: true,
      is_online: true,
      is_desk: true,
      description: "Cocacola normal",
      selection_groups: [
        {
          selection_group_id: 4,
          selection_group_name: "Tamaños Cocacola",
          selection_group_description: "Elige el tamaño de tu cocacola",
          is_price_modifier: false,
          min_variance: 1,
          max_variance: 1,
          min_selections: 1,
          max_selections: 1,
          items: [
            {
              item_id: 400,
              item_name: "Chico",
              price_modifier: 0.00,
              min_selections: 1,
              max_selections: 1
            },
            {
              item_id: 401,
              item_name: "Mediano",
              price_modifier: 0.50,
              min_selections: 1,
              max_selections: 1
            },
            {
              item_id: 402,
              item_name: "Grande",
              price_modifier: 1.00,
              min_selections: 1,
              max_selections: 1
            }
          ]
        }
      ],
      modifiers: [
        {
          modifier_id: 4,
          modifier_name: "Extra Hielo",
          modifier_description: "Agrega extra hielo a tu cocacola",
          is_optional: true,
          is_price_setter: false,
          options: [
            {
              option_id: 8,
              option_name: "Hielo",
              option_description: "Hielo extra",
              price_modifier: 0.00
            }
          ]
        }
      ]
    },
    {
      item_id: 201,
      item_name: "Cocacola Zero",
      label: "Cocacola Zero",
      price: 2.50,
      price_delivery: 3.00,
      price_online: 2.70,
      price_desk: 2.00,
      is_delivery: true,
      is_online: true,
      is_desk: true,
      description: "Cocacola zero",
      selection_groups: [
        {
          selection_group_id: 5,
          selection_group_name: "Tamaños Cocacola Zero",
          selection_group_description: "Elige el tamaño de tu cocacola zero",
          is_price_modifier: false,
          min_variance: 1,
          max_variance: 1,
          min_selections: 1,
          max_selections: 1,
          items: [
            {
              item_id: 500,
              item_name: "Chico",
              price_modifier: 0.00,
              min_selections: 1,
              max_selections: 1
            },
            {
              item_id: 501,
              item_name: "Mediano",
              price_modifier: 0.50,
              min_selections: 1,
              max_selections: 1
            },
            {
              item_id: 502,
              item_name: "Grande",
              price_modifier: 1.00,
              min_selections: 1,
              max_selections: 1
            }
          ]
        }
      ],
      modifiers: [
        {
          modifier_id: 5,
          modifier_name: "Extra Hielo",
          modifier_description: "Agrega extra hielo a tu cocacola zero",
          is_optional: true,
          is_price_setter: false,
          options: [
            {
              option_id: 9,
              option_name: "Hielo",
              option_description: "Hielo extra",
              price_modifier: 0.00
            }
          ]
        }
      ]
    }];

    export const fakeMenu = [
      {
        id: 1,
        name: "Bebidas",
        items: fakeCategoryItems
      },
      {
        id: 2,
        name: "Comidas",
        items: fakeCategoryItems2
      }
    ]
